import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import FilmCard from "@/components/feature/FilmCard";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories } from "@/mocks/categories";
import { films } from "@/mocks/films";
import CategoryFilters from "@/pages/category/components/CategoryFilters";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const category = categories.find((item) => item.key === type);

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("最新上映");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setRegion("全部");
    setYear("全部");
    setSort("最新上映");
    setPage(1);
  }, [type]);

  usePageMeta({
    title: category
      ? `${category.name}三级片电影大全 - 三级片影视资源`
      : "分类未找到 - 三级片电影大全",
    description: category
      ? `提供丰富的${category.name}三级片电影资源，包含经典及最新影片信息，帮您轻松查找相关内容。`
      : "该分类不存在，请返回首页浏览其他三级片影片分类。",
    keywords: category
      ? `${category.name}三级片,${category.name}电影,三级片电影,三级片影视资源`
      : "三级片,三级片电影",
  });

  if (!category) {
    return (
      <SiteLayout>
        <div className="rounded-lg border border-background-200 bg-background-50 px-6 py-16 text-center">
          <i className="ri-folder-unknow-line text-4xl text-background-400" />
          <h1 className="mt-4 text-xl font-bold text-foreground-950">未找到该分类</h1>
          <p className="mt-2 text-sm text-foreground-600">你访问的分类不存在或已下线。</p>
          <Link
            to="/"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const categoryFilms = films.filter((film) => film.categories.includes(category.key));

  const filtered = categoryFilms.filter((film) => {
    const regionMatch = region === "全部" || film.region === region;
    if (!regionMatch) return false;
    if (year === "全部") return true;
    const [start, end] = year.split("-").map(Number);
    return film.year >= start && film.year <= end;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === "热门程度") return b.heat - a.heat;
    if (sort === "评分最高") return b.rating - a.rating;
    return b.year - a.year;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageFilms = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setRegion("全部");
    setYear("全部");
    setSort("最新上映");
    setPage(1);
  };

  return (
    <SiteLayout>
      <div className="rise-in">
        <Breadcrumb items={[{ label: "影片分类", to: "/category/drama" }, { label: `${category.name}片` }]} />

        <header className="mt-4 overflow-hidden rounded-xl border border-background-200">
          <div className="relative h-40 w-full md:h-48">
            <img
              src={category.image}
              alt={`${category.name}三级片分类封面`}
              title={`${category.name}三级片电影大全`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
            <div className="absolute inset-0 flex w-full flex-col justify-end p-5 md:p-7">
              <p className="text-[11px] uppercase tracking-[0.24em] text-secondary-400">
                Genre · {category.en}
              </p>
              <h1 className="mt-1 font-heading text-2xl font-black text-background-50 md:text-3xl">
                {category.name}三级片电影大全
              </h1>
              <p className="mt-2 max-w-2xl text-xs leading-relaxed text-background-200/90 md:text-sm">
                {category.description}
              </p>
            </div>
          </div>
        </header>

        <div className="mt-5">
          <CategoryFilters
            state={{ region, year, sort }}
            onChange={(next) => {
              setRegion(next.region);
              setYear(next.year);
              setSort(next.sort);
              setPage(1);
            }}
            onReset={resetFilters}
          />
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-foreground-600">
            共 <span className="font-medium text-primary-700">{sorted.length}</span> 部影片
            {region !== "全部" && <span className="ml-2 text-xs text-foreground-400">地区：{region}</span>}
            {year !== "全部" && <span className="ml-2 text-xs text-foreground-400">年代：{year}</span>}
          </p>
          <p className="text-xs text-foreground-400">第 {currentPage} / {totalPages} 页</p>
        </div>

        {pageFilms.length > 0 ? (
          <>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5">
              {pageFilms.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="mt-4 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <i className="ri-search-eye-line text-4xl text-background-400" />
            <h2 className="mt-4 text-lg font-bold text-foreground-950">未找到相关影片</h2>
            <p className="mt-2 text-sm text-foreground-600">
              当前筛选条件下没有匹配的{category.name}片，试试更换地区或年代。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line" />
              重置筛选条件
            </button>
          </div>
        )}

        <section className="mt-12 rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
            <i className="ri-links-line text-accent-600" />
            其他分类
          </h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {categories
              .filter((item) => item.key !== category.key)
              .map((item) => (
                <Link
                  key={item.key}
                  to={`/category/${item.key}`}
                  className="cursor-pointer whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                >
                  {item.name}三级片
                </Link>
              ))}
          </div>
          <p className="mt-4 text-xs leading-relaxed text-foreground-500">
            提示：站内影片资料均为虚构演示内容，不提供在线播放、下载或任何受限服务。点击影片卡片可查看完整资料与相关推荐。
          </p>
        </section>
      </div>
    </SiteLayout>
  );
}